import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faUnlockAlt, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { Col, Row, Form, Card, Button, Container, InputGroup, Alert, Image } from '@themesberg/react-bootstrap';
import { Link, useLocation, useHistory } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";

import { Routes } from "../../routes";
import BgImage from "../../assets/img/illustrations/signin.svg";
import ProductivosLogo from "../../assets/img/productivos-logo.png";
import api from "../../api/api";

export default () => {
  const location = useLocation();
  const history = useHistory();

  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    // Extract code from URL query parameters
    const params = new URLSearchParams(location.search);
    const codeParam = params.get('code');
    if (codeParam) {
      setCode(codeParam);
    }
  }, [location]);

  const validateForm = () => {
    const errors = {};

    if (!code.trim()) {
      errors.code = 'El código de recuperación es requerido';
    }

    if (!newPassword) {
      errors.newPassword = 'La nueva contraseña es requerida';
    } else if (newPassword.length < 6) {
      errors.newPassword = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (!captchaValue) {
      errors.captcha = 'Por favor, complete el CAPTCHA';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Call the reset password API endpoint
      const response = await api.post('/api/user/reset-password', null, {
        params: {
          code: code,
          newPassword: newPassword
        }
      });

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        history.push(Routes.Signin.path);
      }, 3000);

    } catch (err) {
      console.error('Reset password error:', err);

      // Extract error message from API response
      let errorMessage = 'No se pudo restablecer la contraseña. El código puede haber expirado o ser inválido.';

      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (validationErrors.captcha) {
      setValidationErrors(prev => ({
        ...prev,
        captcha: ''
      }));
    }
  };

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <p className="text-center">
            <Card.Link as={Link} to={Routes.Signin.path} className="text-gray-700">
              <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Volver al inicio de sesión
            </Card.Link>
          </p>
          <Row className="justify-content-center form-bg-image" style={{ backgroundImage: `url(${BgImage})` }}>
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <Image src={ProductivosLogo} alt="Productivos.org" style={{ height: '60px', marginBottom: '20px' }} />
                  <h3 className="mb-0">Restablecer Contraseña</h3>
                  <p className="text-muted mt-2">
                    Ingresa tu código de recuperación y tu nueva contraseña.
                  </p>
                </div>

                {success && (
                  <Alert variant="success" className="text-center">
                    <FontAwesomeIcon icon={faCheckCircle} size="3x" className="mb-3 d-block" />
                    <h5 className="mb-3">¡Contraseña restablecida exitosamente!</h5>
                    <p className="mb-0">
                      Tu contraseña ha sido actualizada. Serás redirigido al inicio de sesión en 3 segundos...
                    </p>
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger" onClose={() => setError("")} dismissible>
                    <strong>Error</strong>
                    <p className="mb-0 mt-2">{error}</p>
                  </Alert>
                )}

                {!success && (
                  <Form onSubmit={handleSubmit}>
                    {/* Code Field */}
                    <Form.Group id="code" className="mb-3">
                      <Form.Label>Código de Recuperación</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          autoFocus={!code}
                          required
                          type="text"
                          placeholder="Ingrese el código recibido por email"
                          value={code}
                          readOnly
                          disabled
                          isInvalid={!!validationErrors.code}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.code}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* New Password */}
                    <Form.Group id="newPassword" className="mb-3">
                      <Form.Label>Nueva Contraseña</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="password"
                          placeholder="Ingrese su nueva contraseña"
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            if (validationErrors.newPassword) {
                              setValidationErrors(prev => ({ ...prev, newPassword: '' }));
                            }
                          }}
                          isInvalid={!!validationErrors.newPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.newPassword}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* Confirm Password */}
                    <Form.Group id="confirmPassword" className="mb-3">
                      <Form.Label>Confirmar Contraseña</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="password"
                          placeholder="Confirme su nueva contraseña"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (validationErrors.confirmPassword) {
                              setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
                            }
                          }}
                          isInvalid={!!validationErrors.confirmPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.confirmPassword}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>

                    {/* reCAPTCHA */}
                    <Form.Group className="mb-3 d-flex justify-content-center">
                      <div>
                        <ReCAPTCHA
                          sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                          onChange={onCaptchaChange}
                        />
                        {validationErrors.captcha && (
                          <div className="text-danger small mt-1">
                            {validationErrors.captcha}
                          </div>
                        )}
                      </div>
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={loading}
                    >
                      {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
                    </Button>
                  </Form>
                )}

                <div className="d-flex justify-content-center align-items-center mt-4">
                  <span className="fw-normal">
                    ¿No recibiste el código?
                    <Card.Link as={Link} to={Routes.ForgotPassword.path} className="fw-bold">
                      {` Solicitar nuevo código `}
                    </Card.Link>
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};
